// alternative-dashboard/alternative-components/UpcomingObligations.tsx
import { useEffect, useState } from "react";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import { gxIsArray } from "@/app/utils/helpers";
import { gxDashboardUpcomingPaymentsApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import { mapUpcomingObligations } from "../mappers/upcomingObligationsMapper";
import type {
  DashboardUpcomingPaymentsPayload,
  DashboardUpcomingPaymentsResponse,
  UpcomingObligationItem,
} from "@/app/types/alterNativeDashboardLayout";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import NoDataFound from "@/app/components/common/warning/NoDataFound";
import { t } from "i18next";

interface UpcomingObligationsProps {
  title?: string;
}

const formatPaymentDate = (date: string) =>
  new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const getRemainingDays = (date: string) => {
  const today = new Date();

  today.setHours(0, 0, 0, 0);

  const payment = new Date(date);

  payment.setHours(0, 0, 0, 0);

  const diff = Math.ceil(
    (payment.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );

  return diff;
};

const renderObligationItem = (
  item: UpcomingObligationItem,
  isFirst: boolean,
  isLast: boolean,
  formatAmount: (item: UpcomingObligationItem) => string,
) => {
  const currencySymbol = gxCurrencyFormat(0, item.currency).symbol;

  return (
    <div className="gx-uo-item" key={item.id}>
      <div className="gx-uo-marker-col">
        <span
          className="gx-uo-dot"
          style={{
            borderColor: isFirst ? "#F97316" : "#3B82F6",
          }}
        />
        {!isLast && <span className="gx-uo-line" />}
      </div>

      <div className="gx-uo-content">
        <p className="gx-p10-m gx-uo-date">
          {item.paymentDate && formatPaymentDate(item.paymentDate)}

          {item.paymentDate && getRemainingDays(item.paymentDate) >= 0 && (
            <span className="gx-uo-due-badge">
              {" "}
              · in {getRemainingDays(item.paymentDate)} days
            </span>
          )}
        </p>

        <p className="gx-p14-b gx-uo-title">
          {item.title}

          <span className="gx-uo-amount">
            {" - "}
            {currencySymbol} {formatAmount(item)}
          </span>
        </p>
      </div>
    </div>
  );
};

function UpcomingObligations({ title }: UpcomingObligationsProps) {
  const [obligations, setObligations] = useState<UpcomingObligationItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadUpcomingObligations = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const payload: DashboardUpcomingPaymentsPayload = {};
        const result = await gxDashboardUpcomingPaymentsApi(payload);

        if (!isMounted) return;

        const rawData = (
          gxIsArray(result?.data) ? result.data : []
        ) as DashboardUpcomingPaymentsResponse[];

        setObligations(mapUpcomingObligations(rawData));
      } catch (err) {
        if (isMounted) {
          setError(t("upcoming_obligations_fetch_error"));
          setObligations([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadUpcomingObligations();

    return () => {
      isMounted = false;
    };
  }, []);

  const formatAmount = (item: UpcomingObligationItem) =>
    gxCurrencyFormat(item.amount, item.currency).integer;

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="gx-bene-form-container-loading-overlay">
          <SubmitLoading />
        </div>
      );
    }

    if (error) {
      return <p className="gx-p14-r gx-error-text">{error}</p>;
    }

    if (obligations.length === 0) {
      return <NoDataFound message="No Upcoming Obligations" />;
    }

    return (
      <div className="gx-uo-timeline">
        {obligations.map((item, index) =>
          renderObligationItem(
            item,
            index === 0,
            index === obligations.length - 1,
            formatAmount,
          ),
        )}
      </div>
    );
  };

  return (
    <div className="gx-card-box-alter gx-main-upcoming-obligations">
      <h2 className="gx-h4-b">{title || t("upcoming_obligations")}</h2>

      {renderContent()}
    </div>
  );
}

export default UpcomingObligations;
